import { useState, useEffect } from 'react'
import { Component } from '../types/component'

interface PageData {
  id: string
  name: string
  slug: string
  components: Component[]
  metadata: Record<string, any>
  themeConfig?: Record<string, any>
}

interface UsePageOptions {
  apiUrl: string
  storeId: string
  pageSlug: string
  locale?: string
}

interface UsePageResult {
  page: PageData | null
  isLoading: boolean
  error: Error | null
}

/**
 * Hook to fetch and manage page data
 */
export function usePage({ apiUrl, storeId, pageSlug, locale = 'en' }: UsePageOptions): UsePageResult {
  const [page, setPage] = useState<PageData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let mounted = true

    const fetchPage = async () => {
      try {
        setIsLoading(true)
        setError(null)

        // Normalize apiUrl - remove trailing slash and /api/v1 if present
        let normalizedUrl = apiUrl.replace(/\/$/, '')
        if (normalizedUrl.endsWith('/api/v1')) {
          normalizedUrl = normalizedUrl.slice(0, -7) // Remove /api/v1
        }

        const response = await fetch(
          `${normalizedUrl}/api/v1/pages/public/${storeId}/${pageSlug}?locale=${locale}`
        )

        if (!response.ok) {
          throw new Error(`Failed to fetch page: ${response.statusText}`)
        }

        const data = await response.json()

        if (mounted) {
          setPage(data)
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Unknown error'))
        }
      } finally {
        if (mounted) {
          setIsLoading(false)
        }
      }
    }

    fetchPage()

    return () => {
      mounted = false
    }
  }, [apiUrl, storeId, pageSlug, locale])

  return { page, isLoading, error }
}
